// src/Layouts/NavBarHome.js

import React, { useState, useEffect } from 'react';
import { HashLink } from 'react-router-hash-link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faTimes,
  faChevronDown,
  faArrowRightToBracket,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';

import {
  faBallotCheck,
  faMagnifyingGlassWaveform,
  faArrowUpRightDots,
  faHandsHoldingChild,
  faBookBookmark,
  faMicrochipAi,
  faLock,
  faChartNetwork,
  faFileCircleInfo,
  faSolarSystem,
  faPeopleLine,
  faPersonCirclePlus,
  faPersonArrowUpFromLine,
} from '@fortawesome/pro-regular-svg-icons';

// Live Typeform ID
const TYPEFORM_ID = '01JWZQPWXXM5K5FY9XAQ8857MW';

/* ───────────── ÍCONOS BASE (EN) ───────────── */
const ICONS_BASE = {
  Overview: faBallotCheck,
  Analyze: faMagnifyingGlassWaveform,
  Develop: faPersonArrowUpFromLine,
  Follow: faHandsHoldingChild,
  Grow: faArrowUpRightDots,
  Framework: faBookBookmark,
  AI: faMicrochipAi,
  'Security & Compliance': faLock,
  'Bether Network': faChartNetwork,
  'Resource Library': faFileCircleInfo,
  'Why?': faSolarSystem,
  'Who we are?': faPeopleLine,
  Careers: faPersonCirclePlus,
  Pricing: faArrowRightToBracket,
};

/* ───────────── ÍCONOS (incluye ES apuntando a ICONS_BASE) ───────────── */
const SUB_ICONS = {
  ...ICONS_BASE,
  IA: ICONS_BASE.AI,
  'Seguridad y Cumplimiento': ICONS_BASE['Security & Compliance'],
  'Visión general': ICONS_BASE.Overview,
  Analizar: ICONS_BASE.Analyze,
  Desarrollar: ICONS_BASE.Develop,
  Seguir: ICONS_BASE.Follow,
  Grow: ICONS_BASE.Grow,
  'Red Bether': ICONS_BASE['Bether Network'],
  Biblioteca: ICONS_BASE['Resource Library'],
  '¿Por qué?': ICONS_BASE['Why?'],
  '¿Quiénes somos?': ICONS_BASE['Who we are?'],
  Carreras: ICONS_BASE.Careers,
  Precios: ICONS_BASE.Pricing,
};

/* ───────────── TRADUCCIONES DE ETIQUETAS ───────────── */
const LABELS_ES = {
  Product: 'Producto',
  Platform: 'Plataforma',
  Resources: 'Recursos',
  Company: 'Compañía',
  Pricing: 'Precios',
  Overview: 'Visión general',
  Analyze: 'Analizar',
  Develop: 'Desarrollar',
  Follow: 'Seguir',
  Grow: 'Grow',
  Framework: 'Framework',
  AI: 'IA',
  'Security & Compliance': 'Seguridad y Cumplimiento',
  'Bether Network': 'Red Bether',
  'Resource Library': 'Biblioteca',
  'Why?': '¿Por qué?',
  'Who we are?': '¿Quiénes somos?',
  Careers: 'Carreras',
};

/* ───────────── MENÚ BASE EN / ES ───────────── */
const MENU_EN = [
  {
    label: 'Product',
    children: ['Overview', 'Analyze', 'Develop', 'Follow', 'Grow'],
  },
  { label: 'Platform', children: ['Framework', 'AI', 'Security & Compliance'] },
  { label: 'Resources', children: ['Bether Network', 'Resource Library'] },
  { label: 'Company', children: ['Why?', 'Who we are?', 'Careers'] },
  { label: 'Pricing' },
];
const MENU_ES = MENU_EN.map(({ label, children }) => ({
  label: LABELS_ES[label],
  children: children ? children.map((c) => LABELS_ES[c]) : undefined,
}));

/* ───────────── TRADUCCIONES GENERALES ───────────── */
const TRANSLATIONS = {
  en: {
    menu: MENU_EN,
    login: 'Log In',
    demo: 'Book Demo',
  },
  es: {
    menu: MENU_ES,
    login: 'Iniciar sesión',
    demo: 'Reservar Demo',
  },
};

/* ───────────── COMPONENTE NAVBAR ───────────── */
export default function NavBarHome({ language = 'en' }) {
  const t = TRANSLATIONS[language] ?? TRANSLATIONS.en;
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDD, setOpenDD] = useState(null);
  const [mobileOpenDD, setMobileOpenDD] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Detect responsive
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Detect scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const close = (e) => !e.target.closest('.genesy-nav') && setOpenDD(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Inject Typeform script & hide native button
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      div[data-tf-live="${TYPEFORM_ID}"] > * {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    script.onload = () => {
      setTimeout(() => {
        const tfContainer = document.querySelector(
          `div[data-tf-live="${TYPEFORM_ID}"]`
        );
        if (!tfContainer) return;
        const tfBtn = tfContainer.querySelector('button, a');
        if (tfBtn) {
          tfBtn.innerText = language === 'es' ? 'Regístrate' : 'Register';
          Object.assign(tfBtn.style, {
            backgroundColor: '#ff2300',
            color: '#ffffff',
            border: 'none',
            borderRadius: '4px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
          });
        }
      }, 500);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      document.head.removeChild(style);
    };
  }, [language]);

  // Trigger HubSpot (ANTES: Typeform)
  const handleDemoClick = () => {
    if (typeof window !== 'undefined') {
      window.open(
        'https://tdgrm.share.hsforms.com/2o1NiMr-WRiWEniTAEpv4KQ',
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const toggleDD = (label) => setOpenDD(openDD === label ? null : label);
  const toggleMobileDD = (label) =>
    setMobileOpenDD(mobileOpenDD === label ? null : label);

  const getLink = (label) => {
    if (label === 'AI' || label === 'IA') return '/ai';
    if (label === 'Analyze' || label === 'Analizar') return '/analyze';
    if (label === 'Develop' || label === 'Desarrollar') return '/develop';
    if (label === 'Follow' || label === 'Seguir') return '/follow';
    if (label === 'Grow') return '/grow';
    if (label === 'Framework') return '/framework';
    if (
      label === 'Security & Compliance' ||
      label === 'Seguridad y Cumplimiento'
    )
      return '/privacy';
    if (label === 'Bether Network' || label === 'Red Bether') return '/network';
    if (label === 'Resource Library' || label === 'Biblioteca')
      return '/library';
    if (label === 'Why?' || label === '¿Por qué?') return '/why';
    if (label === 'Who we are?' || label === '¿Quiénes somos?') return '/who';
    if (label === 'Careers' || label === 'Carreras') return '/careers';
    if (label === 'Pricing' || label === 'Precios') return '/pricing';
    return `/#${label.toLowerCase().replace(/\s+/g, '-')}`;
  };

  return (
    <>
      <style>{css}</style>

      {/* Hidden Typeform container */}
      <div data-tf-live={TYPEFORM_ID} style={{ display: 'none' }}></div>

      <header className="genesy-wrapper">
        <nav className={`genesy-nav ${scrolled ? 'scrolled' : ''}`}>
          <HashLink to="/" className="logo">
            <img src="/BetherLogoLuminousOrange.png" alt="Bether logo" />
          </HashLink>

          {isMobile ? (
            <button
              className="burger"
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setMobileOpenDD(null);
              }}
            >
              <FontAwesomeIcon icon={mobileOpen ? faTimes : faBars} />
            </button>
          ) : (
            <>
              <ul className="nav-links fw">
                {t.menu.map(({ label, children }) => (
                  <li key={label}>
                    {!children ? (
                      <HashLink to={getLink(label)} className="nav-trigger">
                        {label}
                      </HashLink>
                    ) : (
                      <button
                        type="button"
                        className="nav-trigger"
                        onClick={() => toggleDD(label)}
                      >
                        {label}
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={
                            openDD === label ? 'chevron open' : 'chevron'
                          }
                        />
                      </button>
                    )}

                    {children && openDD === label && (
                      <MegaMenu items={children} getLink={getLink} />
                    )}
                  </li>
                ))}
              </ul>

              <div className="cta">
                <HashLink to="/register" className="btn btn-white fw">
                  <FontAwesomeIcon icon={faArrowRightToBracket} /> {t.login}
                </HashLink>

                <button onClick={handleDemoClick} className="btn btn-orange fw">
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> {t.demo}
                </button>
              </div>
            </>
          )}
        </nav>

        {isMobile && mobileOpen && (
          <div className="mobile-panel">
            {t.menu.map(({ label, children }) => (
              <React.Fragment key={label}>
                {children ? (
                  <>
                    <button
                      type="button"
                      className="mobile-trigger"
                      onClick={() => toggleMobileDD(label)}
                    >
                      <span>{label}</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={
                          mobileOpenDD === label ? 'chevron open' : 'chevron'
                        }
                      />
                    </button>
                    {mobileOpenDD === label && (
                      <ul className="mobile-sub">
                        {children.map((c) => (
                          <li key={c}>
                            <HashLink
                              to={getLink(c)}
                              onClick={() => setMobileOpen(false)}
                              className="mobile-sub-link"
                            >
                              <FontAwesomeIcon
                                icon={SUB_ICONS[c]}
                                className="mega-icon"
                              />
                              {c}
                            </HashLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <HashLink
                    to={getLink(label)}
                    onClick={() => setMobileOpen(false)}
                    className="mobile-trigger no-children"
                  >
                    {label}
                  </HashLink>
                )}
              </React.Fragment>
            ))}

            <HashLink
              to="/register"
              className="btn btn-white full fw"
              onClick={() => setMobileOpen(false)}
            >
              <FontAwesomeIcon icon={faArrowRightToBracket} /> {t.login}
            </HashLink>

            <button
              onClick={() => {
                handleDemoClick();
                setMobileOpen(false);
              }}
              className="btn btn-orange full fw keep-white-text"
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} /> {t.demo}
            </button>
          </div>
        )}
      </header>
    </>
  );
}

function MegaMenu({ items, getLink }) {
  return (
    <div className="mega">
      {items.map((it) => (
        <HashLink key={it} to={getLink(it)} className="mega-link">
          <FontAwesomeIcon icon={SUB_ICONS[it]} className="mega-icon" />
          {it}
        </HashLink>
      ))}
    </div>
  );
}

/* ──────────── CSS embebido ──────────── */
const css = `
.genesy-wrapper { position:fixed; top:8px; left:0; right:0; z-index:1200; }
body { padding-top:96px; }

.genesy-nav {
  width:100%; max-width:1280px; margin:0 auto; background:#fff;
  border:1px solid #e5e7eb; border-radius:16px; padding:0 24px; height:64px;
  display:flex; align-items:center; justify-content:space-between;
  box-shadow:0 4px 12px rgba(0,0,0,.04); font-family:'Helvetica Neue',Arial,sans-serif;
  transition:opacity .25s;
}
.genesy-nav.scrolled { opacity:1; }

.logo img { height:28px; }

.nav-links { list-style:none; display:flex; gap:32px; font-size:16px; margin:0; padding:0; }
.nav-links li { position:relative; }
.fw { font-weight:500; }
.nav-trigger { display:flex; align-items:center; gap:6px; background:none; border:none; cursor:pointer; color:#444; transition:color .2s; text-decoration:none; }
.nav-trigger:hover { color:#000; }
.chevron { transition:transform .25s; }
.chevron.open { transform:rotate(180deg); }

.nav-links li .mega {
  position:absolute; top:calc(100% + 8px); left:0;
  background:#fff; border:1px solid #e5e7eb;
  border-radius:16px; box-shadow:0 8px 24px rgba(0,0,0,.08);
  padding:16px 24px; display:flex; flex-direction:column; gap:12px; min-width:220px;
  z-index:1000;
}
.mega-link {
  display:flex; align-items:center; gap:10px;
  color:#444; font-size:15px; text-decoration:none;
}
.mega-link:hover { color:#000; text-decoration:underline; }
.mega-icon { color:#ff2300; font-size:17px; }

.cta { display:flex; align-items:center; gap:14px; }
.btn {
  display:inline-flex; align-items:center; gap:8px; padding:8px 20px;
  border-radius:8px; font-size:15px; border:2px solid transparent; text-decoration:none;
  transition:all .25s ease;
}
.btn-white { background:#fff; color:#000; border-color:#000; }
.btn-white:hover { background:#f3f4f6; }
.btn-orange { background:#ff2300; border-color:#ff2300; color:#fff; }
.btn-orange:hover { opacity:.9; }
.full { width:100%; justify-content:center; }

.burger { background:none; border:none; font-size:26px; cursor:pointer; }

.mobile-panel {
  position:absolute; top:64px; left:0; right:0; background:#fff; border:1px solid #e5e7eb;
  border-radius:0 0 16px 16px; padding:12px 16px;
  display:flex; flex-direction:column; gap:8px;
}

.mobile-trigger {
  display:flex; align-items:center; justify-content:space-between;
  width:100%; background:none; border:none;
  padding:8px 0; font-size:16px; color:#000;
  cursor:pointer; font-weight:400; transition:color .2s;
}
.mobile-trigger:hover { color:#444; }

.no-children {
  text-decoration:none; color:#000; padding:8px 0;
  display:block; font-weight:400;
}

.mobile-sub {
  list-style:none; margin:4px 0 0 12px; padding:0;
}
.mobile-sub-link {
  display:flex; align-items:center; gap:8px;
  color:#555; padding:4px 0; text-decoration:none; font-weight:400;
}
.mobile-sub-link:hover { color:#000; }
.mobile-sub .mega-icon { color:#ff2300; font-size:16px; }

.mobile-panel .chevron { transition:transform .25s; }
.mobile-panel .chevron.open { transform:rotate(180deg); }

.keep-white-text { color: #fff !important; }

@media(max-width:768px) {
  .nav-links, .cta { display: none; }
}
`;
